import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

/**
 * Liste sayfalama bileşeni.
 * Props:
 *   page, totalPages, total, pageSize, onPageChange, onPageSizeChange, loading
 *   pageSizeOptions: [25, 50, 100, 250, 500, 1000, 2000]
 *   testIdPrefix: data-testid prefix (örn 'ekipman-pagination')
 */
export default function Pagination({
  page = 1,
  totalPages = 0,
  total = 0,
  pageSize = 100,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100, 250, 500, 1000, 2000],
  loading = false,
  testIdPrefix = 'pagination',
}) {
  if (totalPages <= 0 && total === 0) return null;

  const canPrev = page > 1 && !loading;
  const canNext = page < totalPages && !loading;
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  // Görünür sayfa numaraları (max 5)
  const pages = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    const window2 = 2;
    pages.push(1);
    let s = Math.max(2, page - window2);
    let e = Math.min(totalPages - 1, page + window2);
    if (s > 2) pages.push('...');
    for (let i = s; i <= e; i++) pages.push(i);
    if (e < totalPages - 1) pages.push('...');
    pages.push(totalPages);
  }

  const btn = "h-8 min-w-[32px] px-2 rounded-md text-sm border border-gray-700 text-gray-200 hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors";

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-1 py-2" data-testid={testIdPrefix}>
      <div className="text-xs text-gray-400" data-testid={`${testIdPrefix}-info`}>
        {total > 0 ? (
          <>
            <span className="text-gray-200 font-medium">{start.toLocaleString('tr-TR')}-{end.toLocaleString('tr-TR')}</span>
            <span className="text-gray-500"> / {total.toLocaleString('tr-TR')} kayıt</span>
          </>
        ) : (
          <span>Kayıt yok</span>
        )}
      </div>

      <div className="flex items-center gap-1 flex-wrap sm:mr-32" data-testid={`${testIdPrefix}-controls`}>
        {onPageSizeChange && (
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
            className="h-8 px-2 rounded-md text-sm bg-gray-800 border border-gray-700 text-gray-200 mr-2"
            data-testid={`${testIdPrefix}-pagesize`}
          >
            {pageSizeOptions.map(n => (
              <option key={n} value={n}>{n} / sayfa</option>
            ))}
          </select>
        )}

        <button type="button" className={btn} disabled={!canPrev} onClick={() => onPageChange(1)} title="İlk sayfa" data-testid={`${testIdPrefix}-first`}>
          <ChevronsLeft className="w-4 h-4" />
        </button>
        <button type="button" className={btn} disabled={!canPrev} onClick={() => onPageChange(page - 1)} title="Önceki" data-testid={`${testIdPrefix}-prev`}>
          <ChevronLeft className="w-4 h-4" />
        </button>

        {pages.map((p, i) => p === '...' ? (
          <span key={`e${i}`} className="text-gray-500 text-sm px-1">…</span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onPageChange(p)}
            disabled={loading}
            className={`${btn} ${p === page ? 'bg-blue-600 border-blue-500 text-white hover:bg-blue-600' : ''}`}
            data-testid={`${testIdPrefix}-page-${p}`}
          >
            {p}
          </button>
        ))}

        <button type="button" className={btn} disabled={!canNext} onClick={() => onPageChange(page + 1)} title="Sonraki" data-testid={`${testIdPrefix}-next`}>
          <ChevronRight className="w-4 h-4" />
        </button>
        <button type="button" className={btn} disabled={!canNext} onClick={() => onPageChange(totalPages)} title="Son sayfa" data-testid={`${testIdPrefix}-last`}>
          <ChevronsRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
